import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, UserPlus, Users, UserCheck, ArrowRight } from 'lucide-react';
import type { Contact } from '@/types/contacts-new';
import { simpleContactService, type SimpleContact } from '@/services/contactService-simple';
import { QuickAddBorrowerModal } from '../contacts/QuickAddBorrowerModal';
import { toast } from 'sonner';

interface ContactSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContactSelected: (contact: Contact) => void;
}

// Convert SimpleContact to Contact format for compatibility
const convertSimpleContactToContact = (simpleContact: SimpleContact): Contact => {
  return {
    id: simpleContact.id,
    organizationId: simpleContact.organizationId,
    contactType: simpleContact.contactType as 'borrower' | 'referral_partner',
    title: simpleContact.title, // Include title for formal addressing
    firstName: simpleContact.firstName || '',
    lastName: simpleContact.lastName || '',
    email: simpleContact.email,
    mobilePhone: simpleContact.mobilePhone,
    workPhone: simpleContact.workPhone,
    homePhone: simpleContact.homePhone,
    mailingStreet: simpleContact.mailingStreet,
    mailingCity: simpleContact.mailingCity,
    mailingState: simpleContact.mailingState,
    mailingZip: simpleContact.mailingZip,
    dateOfBirth: simpleContact.dateOfBirth,
    company: simpleContact.company,
    notes: simpleContact.notes,
    tags: simpleContact.tags || [],
    source: simpleContact.source || 'manual',
    status: simpleContact.status || 'new',
    createdAt: simpleContact.createdAt,
    updatedAt: simpleContact.updatedAt
  };
};

export const ContactSelectionModal: React.FC<ContactSelectionModalProps> = ({
  open,
  onOpenChange,
  onContactSelected
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedView, setSelectedView] = useState<'select' | 'create'>('select');

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      
      // Use simpleContactService to get contacts from contacts_simple table
      const simpleContactList = await simpleContactService.list({ 
        search: searchTerm 
      });
      // Convert SimpleContact[] to Contact[] for compatibility
      const contactList = simpleContactList.map(convertSimpleContactToContact);
      
      setContacts(contactList);
      return contactList; // Return the fresh contact list
    } catch (__error) {
      console.error('Error loading contacts:', __error);
      toast.error('Failed to load contacts');
      return [];
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Load contacts when modal opens
  useEffect(() => {
    if (open) {
      loadContacts();
    }
  }, [open, loadContacts]);

  // Search contacts as user types
  useEffect(() => {
    const timer = setTimeout(() => {
      if (open) {
        loadContacts();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [open, searchTerm, loadContacts]);

  const handleContactSelect = async (contact: Contact) => {
    try {
      // For now, just pass the contact without lead management
      // since the leads table doesn't exist in the simplified schema
      // The parent will handle the pre-approval creation.
      onContactSelected(contact);
      onOpenChange(false);

      toast.success(`Selected ${contact.firstName} ${contact.lastName} for pre-approval`);
    } catch (__error) {
      console.error('Error selecting contact:', __error);
      toast.error('Failed to process contact selection');
    }
  };

  const getLifecycleIcon = (_contact: Contact) => {
    // This is a simplified version - in reality you'd check leads/applications/borrowers
    return <UserCheck className="w-4 h-4" />;
  };

  const getLifecycleStage = (_contact: Contact) => {
    // This is simplified - you'd actually check the related records
    return 'Lead';
  };

  return (
    <>
      <Dialog open={open && !showCreateModal} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              Select or Create Contact for Pre-Approval
            </DialogTitle>
            <DialogDescription>
              Choose an existing contact or create a new one for this pre-approval.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* View Toggle */}
            <div className="flex gap-2">
              <Button
                variant={selectedView === 'select' ? 'default' : 'outline'}
                onClick={() => setSelectedView('select')}
                className="flex-1"
              >
                <Users className="w-4 h-4 mr-2" />
                Select Existing Contact
              </Button>
              <Button
                variant={selectedView === 'create' ? 'default' : 'outline'}
                onClick={() => setSelectedView('create')}
                className="flex-1"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Create New Contact
              </Button>
            </div>

            {selectedView === 'select' ? (
              <>
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Contact List */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading contacts...
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">
                        {searchTerm ? 'No contacts found matching your search.' : 'No contacts yet.'}
                      </p>
                      <Button onClick={() => setSelectedView('create')}>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create First Contact
                      </Button>
                    </div>
                  ) : (
                    contacts.map((contact) => (
                      <Card 
                        key={contact.id} 
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleContactSelect(contact)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg bg-blue-100 text-blue-700`}>
                                {getLifecycleIcon(contact)}
                              </div>
                              <div>
                                <h4 className="font-semibold">
                                  {contact.firstName} {contact.lastName}
                                </h4>
                                <div className="text-sm text-muted-foreground space-y-1">
                                  {contact.email && <div>{contact.email}</div>}
                                  {contact.mobilePhone && <div>{contact.mobilePhone}</div>}
                                  {contact.mailingStreet && (
                                    <div>
                                      {contact.mailingStreet}
                                      {contact.mailingCity && `, ${contact.mailingCity}`}
                                      {contact.mailingState && `, ${contact.mailingState}`}
                                      {contact.mailingZip && ` ${contact.mailingZip}`}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">
                                {getLifecycleStage(contact)}
                              </Badge>
                              <ArrowRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="py-8 text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <UserPlus className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Create New Contact</h3>
                  <p className="text-muted-foreground mb-6">
                    Add a new contact to your database. A lead will be automatically created.
                  </p>
                </div>
                <Button 
                  size="lg"
                  onClick={() => setShowCreateModal(true)}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Contact
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Add Borrower Modal */}
      <QuickAddBorrowerModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={async () => {
          try {
            // Reload contacts to get the freshly created one
            const freshContacts = await loadContacts();

            // Auto-select the most recently created contact (first in list)
            if (freshContacts.length > 0) {
              const latestContact = freshContacts[0];
              await handleContactSelect(latestContact);
              toast.success(`Contact ${latestContact.firstName} ${latestContact.lastName} imported to pre-approval`);
            }
          } catch {
            toast.error('Contact was created but failed to import automatically');
          } finally {
            setShowCreateModal(false);
            setSelectedView('select');
          }
        }}
      />
    </>
  );
};
